import {
  collection,
  doc,
  getDocs,
  setDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { firestore } from '../config/firebase';

const COLLECTION = 'interestPayments';

/**
 * Clamp fund interest period anchor to 1–28 (avoids month-end ambiguity).
 * @param {unknown} raw
 * @returns {number}
 */
export function normalizeInterestPeriodAnchorDay(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 1;
  return Math.min(28, Math.max(1, Math.floor(n)));
}

function addDaysToYmd(isoDate, deltaDays) {
  const s = String(isoDate || '').slice(0, 10);
  if (s.length < 10) return s;
  const x = new Date(`${s}T12:00:00`);
  x.setDate(x.getDate() + deltaDays);
  const y = x.getFullYear();
  const m = x.getMonth() + 1;
  const d = x.getDate();
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/**
 * Get period key (YYYY-MM) and bounds for a given year/month.
 * @param {number} year
 * @param {number} month (1-12)
 * @returns {{ periodKey: string, periodStart: string, periodEnd: string }}
 */
export function getPeriodForMonth(year, month) {
  const periodKey = `${year}-${String(month).padStart(2, '0')}`;
  const periodStart = `${periodKey}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const periodEnd = `${periodKey}-${String(lastDay).padStart(2, '0')}`;
  return { periodKey, periodStart, periodEnd };
}

/**
 * Fund month: periodKey = YYYY-MM of the period start; period runs from anchor day (inclusive)
 * to the day before the next period’s anchor (inclusive). Anchor 1 = calendar month.
 * @param {number} year
 * @param {number} month (1-12)
 * @param {number} [anchorDay=1]
 * @returns {{ periodKey: string, periodStart: string, periodEnd: string }}
 */
export function getFundPeriodForMonth(year, month, anchorDay = 1) {
  const a = normalizeInterestPeriodAnchorDay(anchorDay);
  if (a === 1) return getPeriodForMonth(year, month);
  const periodKey = `${year}-${String(month).padStart(2, '0')}`;
  const dim = new Date(year, month, 0).getDate();
  const day = Math.min(a, dim);
  const periodStart = `${periodKey}-${String(day).padStart(2, '0')}`;
  let nm = month + 1;
  let ny = year;
  if (nm > 12) {
    nm = 1;
    ny += 1;
  }
  const nextDim = new Date(ny, nm, 0).getDate();
  const nextDay = Math.min(a, nextDim);
  const nextStart = `${ny}-${String(nm).padStart(2, '0')}-${String(nextDay).padStart(2, '0')}`;
  const periodEnd = addDaysToYmd(nextStart, -1);
  return { periodKey, periodStart, periodEnd };
}

/**
 * Which fund period contains dateStr (YYYY-MM-DD).
 * @param {string} dateStr
 * @param {number} [anchorDay=1]
 */
export function getFundPeriodContainingDate(dateStr, anchorDay = 1) {
  const a = normalizeInterestPeriodAnchorDay(anchorDay);
  const ds = String(dateStr || '').slice(0, 10);
  if (!ds || ds.length < 10) {
    const now = new Date();
    return getPeriodForMonth(now.getFullYear(), now.getMonth() + 1);
  }
  if (a === 1) {
    const [y, m] = ds.split('-').map(Number);
    return getPeriodForMonth(y, m);
  }
  const [y, m, day] = ds.split('-').map(Number);
  let py = y;
  let pm = m;
  if (day < a) {
    pm -= 1;
    if (pm < 1) {
      pm = 12;
      py -= 1;
    }
  }
  return getFundPeriodForMonth(py, pm, a);
}

function advanceFundPeriodByMonths(periodKey, deltaMonths, anchorDay) {
  const a = normalizeInterestPeriodAnchorDay(anchorDay);
  const [y, m] = periodKey.split('-').map(Number);
  let nm = m + deltaMonths;
  let ny = y;
  while (nm > 12) {
    nm -= 12;
    ny += 1;
  }
  while (nm < 1) {
    nm += 12;
    ny -= 1;
  }
  return getFundPeriodForMonth(ny, nm, a);
}

/**
 * Current fund period (optional anchor; default = calendar month).
 * @param {{ interestPeriodAnchorDay?: number, now?: Date }} [options]
 */
export function getCurrentPeriod(options = {}) {
  const anchor = normalizeInterestPeriodAnchorDay(options.interestPeriodAnchorDay);
  const now = options.now instanceof Date ? options.now : new Date();
  const iso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return getFundPeriodContainingDate(iso, anchor);
}

/**
 * All interest periods for a deal from startDate to maturityDate.
 * @param {object} deal - { startDate: string, maturityDate: string }
 * @param {{ interestPeriodAnchorDay?: number }} [options]
 * @returns {Array<{ periodKey: string, periodStart: string, periodEnd: string }>}
 */
export function getSchedulePeriodsForDeal(deal, options = {}) {
  const anchor = normalizeInterestPeriodAnchorDay(options.interestPeriodAnchorDay);
  const start = (deal?.startDate || '').slice(0, 10);
  const end = (deal?.maturityDate || '').slice(0, 10);
  if (!start || !end || start > end) return [];
  if (anchor === 1) {
    const [sy, sm] = start.split('-').map(Number);
    const [ey, em] = end.split('-').map(Number);
    const periods = [];
    let y = sy;
    let m = sm;
    while (y < ey || (y === ey && m <= em)) {
      periods.push(getPeriodForMonth(y, m));
      m += 1;
      if (m > 12) {
        m = 1;
        y += 1;
      }
    }
    return periods;
  }
  let cur = getFundPeriodContainingDate(start, anchor);
  const periods = [];
  let safety = 0;
  while (cur.periodStart <= end && safety < 600) {
    safety += 1;
    if (cur.periodEnd >= start) {
      periods.push(cur);
    }
    cur = advanceFundPeriodByMonths(cur.periodKey, 1, anchor);
  }
  return periods;
}

/**
 * Count calendar days between two dates (inclusive). Dates as "YYYY-MM-DD".
 * @param {string} start
 * @param {string} end
 * @returns {number}
 */
function daysBetween(start, end) {
  if (!start || !end) return 0;
  const a = new Date(start + 'T12:00:00');
  const b = new Date(end + 'T12:00:00');
  const diff = Math.round((b - a) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff + 1);
}

/**
 * Expected interest for a period using daily proration (365-day year).
 * Same logic as historical behaviour for `interestCalculationMode !== 'monthly'`.
 * @param {object} deal
 * @param {Array<object>} tranches
 * @param {string} periodStart - "YYYY-MM-DD"
 * @param {string} periodEnd - "YYYY-MM-DD"
 * @returns {number}
 */
export function computeExpectedInterestDailyProration(deal, tranches, periodStart, periodEnd) {
  const dealStart = (deal?.startDate || '').slice(0, 10);
  const dealEnd = (deal?.maturityDate || '').slice(0, 10);
  const effectivePeriodStart = dealStart && dealStart > periodStart ? dealStart : periodStart;
  const effectivePeriodEnd = dealEnd && dealEnd < periodEnd ? dealEnd : periodEnd;
  if (effectivePeriodStart > effectivePeriodEnd) return 0;

  if (!tranches?.length) {
    const amount = deal?.principalAmount ?? 0;
    const rate = deal?.interestRate ?? 0;
    if (!amount || !rate) return 0;
    const days = daysBetween(effectivePeriodStart, effectivePeriodEnd);
    return Math.round((amount * rate / 100 * days / 365) * 100) / 100;
  }
  const fallbackRate = deal?.interestRate ?? 0;
  let total = 0;
  for (const tr of tranches) {
    const trStart = (tr.startDate || '').slice(0, 10);
    if (trStart && trStart > effectivePeriodEnd) continue;
    const start = trStart && trStart > effectivePeriodStart ? trStart : effectivePeriodStart;
    const end = effectivePeriodEnd;
    if (start > end) continue;
    const rate = tr.interestRate != null && !Number.isNaN(Number(tr.interestRate)) ? Number(tr.interestRate) : fallbackRate;
    const amount = Number(tr.amount) || 0;
    if (amount && rate) {
      const days = daysBetween(start, end);
      total += amount * rate / 100 * days / 365;
    }
  }
  return Math.round(total * 100) / 100;
}

function trancheStartsMidCalendarMonth(tranches, periodStart, periodEnd) {
  if (!tranches?.length) return false;
  return tranches.some((tr) => {
    const ts = (tr.startDate || '').slice(0, 10);
    return ts && ts > periodStart && ts <= periodEnd;
  });
}

/**
 * Monthly coupon: nominal annual rate / 12 × principal per tranche (full calendar months only).
 * Falls back to {@link computeExpectedInterestDailyProration} for the first/last loan month when the loan
 * does not cover the full calendar month, or when a tranche starts mid-month.
 * @param {object} deal - interestRate, startDate, maturityDate, interestCalculationMode
 * @param {Array<object>} tranches
 * @param {string} periodStart
 * @param {string} periodEnd
 * @returns {number}
 */
function computeExpectedInterestMonthlyMode(deal, tranches, periodStart, periodEnd) {
  const dealStart = (deal?.startDate || '').slice(0, 10);
  const dealEnd = (deal?.maturityDate || '').slice(0, 10);
  const effectivePeriodStart = dealStart && dealStart > periodStart ? dealStart : periodStart;
  const effectivePeriodEnd = dealEnd && dealEnd < periodEnd ? dealEnd : periodEnd;
  if (effectivePeriodStart > effectivePeriodEnd) return 0;

  const partialAtStart = effectivePeriodStart > periodStart;
  const partialAtEnd = effectivePeriodEnd < periodEnd;
  if (partialAtStart || partialAtEnd) {
    return computeExpectedInterestDailyProration(deal, tranches, periodStart, periodEnd);
  }
  if (trancheStartsMidCalendarMonth(tranches, periodStart, periodEnd)) {
    return computeExpectedInterestDailyProration(deal, tranches, periodStart, periodEnd);
  }

  if (!tranches?.length) {
    const amount = deal?.principalAmount ?? 0;
    const rate = deal?.interestRate ?? 0;
    if (!amount || !rate) return 0;
    return Math.round((amount * rate / 100 / 12) * 100) / 100;
  }
  const fallbackRate = deal?.interestRate ?? 0;
  let total = 0;
  for (const tr of tranches) {
    const trStart = (tr.startDate || '').slice(0, 10);
    if (trStart && trStart > periodStart) continue;
    const rate = tr.interestRate != null && !Number.isNaN(Number(tr.interestRate)) ? Number(tr.interestRate) : fallbackRate;
    const amount = Number(tr.amount) || 0;
    if (amount && rate) total += amount * rate / 100 / 12;
  }
  return Math.round(total * 100) / 100;
}

/**
 * Compute expected interest for a deal for a calendar month period [periodStart, periodEnd].
 * - `interestCalculationMode` `daily` (default): daily proration / 365 (existing behaviour).
 * - `monthly`: fixed month coupon = P×(rate/100)/12 for full months; first/last partial months use daily proration.
 * @param {object} deal - financed deal with interestRate, startDate, maturityDate, interestCalculationMode
 * @param {Array<object>} tranches - financedDealTranches
 * @param {string} periodStart - "YYYY-MM-DD"
 * @param {string} periodEnd - "YYYY-MM-DD"
 * @returns {number}
 */
export function computeExpectedInterestForPeriod(deal, tranches, periodStart, periodEnd) {
  const mode = deal?.interestCalculationMode === 'monthly' ? 'monthly' : 'daily';
  if (mode === 'monthly') {
    return computeExpectedInterestMonthlyMode(deal, tranches, periodStart, periodEnd);
  }
  return computeExpectedInterestDailyProration(deal, tranches, periodStart, periodEnd);
}

/**
 * Get the expected payment date for a given period (YYYY-MM).
 * New offers use end-of-month only; `beginning_of_month` is kept for legacy deals.
 * - end_of_month: last day of the month, or deal start date if first partial month and start is after 1st, or maturity date if loan ends mid-month.
 * - beginning_of_month: first day of the month, or deal start date if first partial month, or maturity date if loan ends mid-month.
 * @param {object} deal - financed deal with startDate, maturityDate, interestPaymentDue
 * @param {string} periodKey - "YYYY-MM"
 * @param {number} [interestPeriodAnchorDay=1] lender fund period anchor (1 = calendar month)
 * @returns {string} "YYYY-MM-DD"
 */
export function getExpectedPaymentDateForPeriod(deal, periodKey, interestPeriodAnchorDay = 1) {
  const anchor = normalizeInterestPeriodAnchorDay(interestPeriodAnchorDay);
  const [y, m] = periodKey.split('-').map(Number);
  const { periodStart, periodEnd } =
    anchor === 1 ? getPeriodForMonth(y, m) : getFundPeriodForMonth(y, m, anchor);
  const dealStart = (deal?.startDate || '').slice(0, 10);
  const dealEnd = (deal?.maturityDate || '').slice(0, 10);
  const isFirstPartialMonth = dealStart && dealStart > periodStart;
  const isLastPartialMonth = dealEnd && dealEnd >= periodStart && dealEnd <= periodEnd;
  if (isLastPartialMonth) {
    return dealEnd;
  }
  const due = deal?.interestPaymentDue === 'beginning_of_month' ? 'beginning_of_month' : 'end_of_month';
  if (due === 'end_of_month') {
    return isFirstPartialMonth ? dealStart : periodEnd;
  }
  return isFirstPartialMonth ? dealStart : periodStart;
}

/**
 * Whether the deal is active during the given period (overlaps [periodStart, periodEnd]).
 * @param {object} deal
 * @param {string} periodStart
 * @param {string} periodEnd
 * @returns {boolean}
 */
export function isDealActiveInPeriod(deal, periodStart, periodEnd) {
  const dealStart = (deal?.startDate || '').slice(0, 10);
  const dealEnd = (deal?.maturityDate || '').slice(0, 10);
  if (dealStart && dealStart > periodEnd) return false;
  if (dealEnd && dealEnd < periodStart) return false;
  return true;
}

/**
 * Get one interest payment document by deal and period key.
 * @param {string} dealId
 * @param {string} periodKey - "YYYY-MM"
 * @param {number} [interestPeriodAnchorDay=1]
 * @returns {Promise<object | null>}
 */
export async function getInterestPayment(dealId, periodKey, interestPeriodAnchorDay = 1) {
  if (!dealId || !periodKey) return null;
  const anchor = normalizeInterestPeriodAnchorDay(interestPeriodAnchorDay);
  const [y, m] = periodKey.split('-').map(Number);
  const { periodStart, periodEnd } =
    anchor === 1 ? getPeriodForMonth(y, m) : getFundPeriodForMonth(y, m, anchor);
  const q = query(
    collection(firestore, COLLECTION),
    where('dealId', '==', dealId),
    where('periodStart', '==', periodStart)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

/**
 * Get all interest payments for a deal (for history).
 * @param {string} dealId
 * @returns {Promise<Array<object>>}
 */
export async function getInterestPaymentsForDeal(dealId) {
  if (!dealId) return [];
  const q = query(
    collection(firestore, COLLECTION),
    where('dealId', '==', dealId)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Create or update an interest payment (mark as received). Lender only.
 * Sets status from paidAmount vs expectedAmount: paid, partial, overpaid.
 * @param {string} dealId
 * @param {string} lenderUid
 * @param {string} periodKey - "YYYY-MM"
 * @param {{ paidAmount: number, paidAt?: Date | null, discrepancyNote?: string }} data
 * @param {number} expectedAmount
 * @param {number} [interestPeriodAnchorDay=1]
 * @returns {Promise<string>} document id
 */
export async function createOrUpdateInterestPayment(dealId, lenderUid, periodKey, data, expectedAmount, interestPeriodAnchorDay = 1) {
  if (!dealId || !lenderUid || periodKey == null) {
    throw new Error('dealId, lenderUid and periodKey required');
  }
  const paid = Number(data.paidAmount);
  if (Number.isNaN(paid) || paid < 0) {
    throw new Error('paidAmount must be a non-negative number');
  }
  const anchor = normalizeInterestPeriodAnchorDay(interestPeriodAnchorDay);
  const [y, m] = periodKey.split('-').map(Number);
  const { periodStart, periodEnd } =
    anchor === 1 ? getPeriodForMonth(y, m) : getFundPeriodForMonth(y, m, anchor);
  const existing = await getInterestPayment(dealId, periodKey, anchor);
  let status = 'pending';
  if (expectedAmount != null && expectedAmount > 0) {
    if (paid >= expectedAmount * 0.999) status = paid <= expectedAmount * 1.001 ? 'paid' : 'overpaid';
    else if (paid > 0) status = 'partial';
  } else if (paid > 0) {
    status = 'paid';
  }
  const payload = {
    dealId,
    lenderUid,
    periodKey,
    periodStart,
    periodEnd,
    expectedAmount: expectedAmount ?? null,
    paidAmount: paid,
    paidAt: data.paidAt ? (data.paidAt.toDate ? data.paidAt.toDate() : new Date(data.paidAt)) : new Date(),
    markedByUid: lenderUid,
    source: 'manual',
    status,
    discrepancyNote: (data.discrepancyNote || '').trim() || null,
    updatedAt: serverTimestamp(),
  };
  if (existing) {
    const ref = doc(firestore, COLLECTION, existing.id);
    await setDoc(ref, payload, { merge: true });
    return existing.id;
  }
  const ref = doc(collection(firestore, COLLECTION));
  await setDoc(ref, { ...payload, createdAt: serverTimestamp() });
  return ref.id;
}
